import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React from "react";
import { Col, Row } from "react-bootstrap";
import { AppSettingsService, UserService } from "src/app/services/Index";
import { FetchInterceptor, safeJson } from "src/app/helpers/Index";
import { environment } from "src/environments/environment";
import "./AdminProfile.css";

type Props = {};
type State = {
	confirmNewPassword: string;
	currentUser: any;
	groups: string[];
	isSaving: boolean;
	newPassword: string;
	oldPassword: string;
	rules: string;
};

export class AdminProfile extends React.Component<Props, State> {
	toast: any;

	constructor(props: Props) {
		super(props);
		this.state = { confirmNewPassword: "", currentUser: null, groups: [], isSaving: false, newPassword: "", oldPassword: "", rules: "" };
	}

	componentDidMount() {
		this.getMe();
		this.getRules();
		this.getGroups();
	}

	async getGroups() {
		const groups = await AppSettingsService.get("groups");
		this.setState({ groups: (groups || "").split(",").filter(Boolean) });
	}

	async getMe() {
		const res = await safeJson(UserService.me());
		if (res && !res.error) this.setState({ currentUser: res });
	}

	async getRules() {
		try {
			const res = await safeJson(FetchInterceptor(`${environment.server.apiURL}/SystemSetting/PasswordComplexityRules`, { headers: { "Content-Type": "application/json" }, method: "GET" }));
			if (res && res.rules) this.setState({ rules: res.rules });
		} catch { /* rules stay hidden */ }
	}

	/** 0-5: length 8+, length 12+, mixed case, digit, symbol */
	strengthScore(password: string) {
		let score = 0;
		if (password.length >= 8) score++;
		if (password.length >= 12) score++;
		if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
		if (/\d/.test(password)) score++;
		if (/[^a-zA-Z0-9]/.test(password)) score++;
		return score;
	}

	strengthMeta(score: number) {
		if (score <= 1) return { colour: "#E61818", label: "Weak" };
		if (score === 2) return { colour: "#F57C00", label: "Fair" };
		if (score === 3) return { colour: "#FFD700", label: "Good" };
		if (score === 4) return { colour: "#0EA371", label: "Strong" };
		return { colour: "#11B981", label: "Very strong" };
	}

	canSubmit() {
		return this.state.oldPassword.length > 0
			&& this.state.newPassword.length > 0
			&& this.state.newPassword === this.state.confirmNewPassword
			&& !this.state.isSaving;
	}

	async changePassword() {
		this.setState({ isSaving: true });
		const res = await UserService.updatePassword({
			confirmNewPassword: this.state.confirmNewPassword,
			newPassword: this.state.newPassword,
			oldPassword: this.state.oldPassword
		} as any);
		this.setState({ isSaving: false });
		if (!res.ok) {
			let detail = "Please check your current password and the rules below.";
			try {
				const body = await res.json();
				if (body?.error) detail = typeof body.error === "string" ? body.error : Object.values(body.error).flat().join(" ");
			} catch { /* keep default */ }
			this.toast.show({ closable: false, detail, life: 6000, severity: "error", summary: "Password not changed" });
			return;
		}
		this.setState({ confirmNewPassword: "", newPassword: "", oldPassword: "" });
		this.toast.show({ closable: false, detail: "Use it next time you sign in.", life: 5000, severity: "success", summary: "Password changed!" });
	}

	render() {
		const user = this.state.currentUser;
		const initials = user ? `${(user.firstName || " ")[0]}${(user.lastName || " ")[0]}`.trim().toUpperCase() : "";
		const score = this.strengthScore(this.state.newPassword);
		const meta = this.strengthMeta(score);
		const mismatch = this.state.confirmNewPassword.length > 0 && this.state.newPassword !== this.state.confirmNewPassword;

		return (
			<>
				<Row>
					<Col lg={5} xs={12}>
						<div className="card profile-card">
							<div className="profile-avatar">{initials}</div>
							<h4 className="profile-name">{user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "…"}</h4>
							<div className="profile-email">{user?.emailAddress}</div>
							<div className="profile-groups">
								{this.state.groups.map((group) => <span className="profile-group" key={group}>{group}</span>)}
							</div>
							{user?.createdAt &&
								<div className="profile-since">Member since {new Date(user.createdAt).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</div>
							}
						</div>
					</Col>
					<Col lg={7} xs={12}>
						<div className="card" style={{ padding: "1.5rem" }}>
							<h5 style={{ marginBottom: "1.25rem" }}>Change Password</h5>
							<div className="field" style={{ marginBottom: 14 }}>
								<label htmlFor="oldPassword">Current password</label>
								<InputText id="oldPassword" onChange={(e) => this.setState({ oldPassword: e.target.value })} style={{ width: "100%" }} type="password" value={this.state.oldPassword} />
							</div>
							<div className="field" style={{ marginBottom: 6 }}>
								<label htmlFor="newPassword">New password</label>
								<InputText id="newPassword" onChange={(e) => this.setState({ newPassword: e.target.value })} style={{ width: "100%" }} type="password" value={this.state.newPassword} />
							</div>
							<div className="strength-bar">
								{[1, 2, 3, 4, 5].map((segment) =>
									<span className="strength-segment" key={segment} style={{ backgroundColor: this.state.newPassword && score >= segment ? meta.colour : "var(--pb-surface-2)" }} />
								)}
								<span className="strength-label" style={{ color: this.state.newPassword ? meta.colour : "var(--pb-text-muted)" }}>{this.state.newPassword ? meta.label : ""}</span>
							</div>
							<div className="field" style={{ marginBottom: 6 }}>
								<label htmlFor="confirmNewPassword">Confirm new password</label>
								<InputText id="confirmNewPassword" onChange={(e) => this.setState({ confirmNewPassword: e.target.value })} onKeyDown={(e) => e.key === "Enter" && this.canSubmit() && this.changePassword()} style={{ width: "100%" }} type="password" value={this.state.confirmNewPassword} />
							</div>
							{mismatch && <div className="password-mismatch">Passwords don't match yet.</div>}
							{this.state.rules && <p className="password-rules">{this.state.rules}</p>}
							<Button disabled={!this.canSubmit()} icon="pi pi-lock" label={this.state.isSaving ? "Changing…" : "Change Password"} loading={this.state.isSaving} style={{ backgroundColor: "#11B981", borderColor: "#11B981", marginTop: 10 }} onClick={() => this.changePassword()}></Button>
						</div>
					</Col>
				</Row>
				<Toast ref={(el) => this.toast = el}></Toast>
			</>
		);
	}
}
